// src/pages/game/ScoreFormRedirect.jsx
// This component redirects from the old scores route to the new match report route

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import unifiedApi from '../../utils/unifiedApi';
import { usePermissions } from '../../contexts/PermissionsContext';

const ScoreFormRedirect = () => {
  const { orgId, leagueId, gameId } = useParams();
  const navigate = useNavigate();
  const { canManageTeam, permissions, error: permissionsError } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permissionsError) {
      setError('Failed to load your permissions');
      setLoading(false);
      return;
    }
    // Wait for permissions to load; otherwise every team check would be false
    if (!permissions) return;

    let cancelled = false;

    const redirectToMatchReport = async () => {
      try {
        // Load game details to get team IDs
        const game = await unifiedApi.getGameById(gameId);
        if (!game) {
          throw new Error('Failed to load game details');
        }
        
        // Determine which team the user can manage
        let targetTeamId = null;
        if (canManageTeam(game.homeTeamId)) {
          targetTeamId = game.homeTeamId;
        } else if (game.awayTeamId && canManageTeam(game.awayTeamId)) {
          targetTeamId = game.awayTeamId;
        } else {
          throw new Error('You do not have permission to manage either team in this game');
        }
        
        if (cancelled) return;
        // Redirect to the new match report page
        navigate(`/organization/${orgId}/league/${leagueId}/team/${targetTeamId}/game/${gameId}/report`, {
          replace: true
        });
        
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to redirect to match report');
        setLoading(false);
      }
    };

    redirectToMatchReport();
    return () => { cancelled = true; };
  }, [orgId, leagueId, gameId, navigate, canManageTeam, permissions, permissionsError]);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '50vh',
        gap: 2
      }}>
        <CircularProgress />
        <Typography>Redirecting to Match Report...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          The score submission has been replaced with a comprehensive match report system. 
          Please use the "Submit Match Report" button from the schedule to access the new functionality.
        </Typography>
      </Box>
    );
  }

  return null;
};

export default ScoreFormRedirect;